/**
 * CD Mutation Hooks
 *
 * TanStack Query mutations that:
 * 1. Call the API to persist changes
 * 2. Update the Zustand store's serverData
 * 3. Invalidate related query caches
 *
 * Use these hooks instead of calling the API directly to keep
 * the store and cache in sync.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { closingDisclosureApi } from '../services/closingDisclosureApi';
import { useClosingDisclosureStore } from '../store/closingDisclosureStore';
import type { LineItem, LoanTerms, Party, Transaction } from '../types';
import { cdQueryKeys } from './useClosingDisclosureQueries';

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Mutation to create a new transaction
 */
export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<Transaction, 'id' | 'created_at' | 'updated_at'>) => {
      const result = await closingDisclosureApi.create(data);
      return { ...data, id: result.data.transactionId } as Transaction;
    },
    onSuccess: (transaction) => {
      // Use getState() to get fresh store reference inside callback
      const store = useClosingDisclosureStore.getState();

      // Update store
      store.updateServerTransaction(transaction);

      // Hydrate store with new transaction (initializes empty related data)
      store.hydrateFromServer({
        transaction,
        loanTerms: null,
        parties: [],
        lineItems: [],
        calculations: null,
      });

      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/**
 * Mutation to update an existing transaction
 */
export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, transaction }: { id: string; transaction: Partial<Transaction> }) => {
      await closingDisclosureApi.update(id, transaction);
      return { id, transaction };
    },
    onSuccess: ({ id, transaction }) => {
      // Use getState() to get fresh store reference inside callback
      const store = useClosingDisclosureStore.getState();

      // Get current transaction and merge with updates
      const currentTransaction = store.serverData.transaction;
      if (currentTransaction && currentTransaction.id === id) {
        store.updateServerTransaction({ ...currentTransaction, ...transaction } as Transaction);
      }

      // Invalidate transaction and list queries
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transaction(id) });
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transactions() });
      // Note: Calculations refresh is handled at the page level after all mutations complete
    },
  });
}

/**
 * Mutation to save loan terms
 */
export function useSaveLoanTerms() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      loanTerms,
    }: {
      transactionId: string;
      loanTerms: Omit<LoanTerms, 'id' | 'transaction_id'>;
    }) => {
      const result = await closingDisclosureApi.saveLoanTerms(transactionId, loanTerms);
      return result.data;
    },
    onSuccess: (loanTerms, { transactionId }) => {
      // Use getState() to get fresh store reference inside callback
      const store = useClosingDisclosureStore.getState();

      // Update store
      store.updateServerLoanTerms(loanTerms);

      // Invalidate loan terms and list queries
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.loanTerms(transactionId) });
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transactions() });
      // Note: Calculations refresh is handled at the page level after all mutations complete
    },
  });
}

/**
 * Mutation to save parties
 */
export function useSaveParties() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      parties,
    }: {
      transactionId: string;
      parties: Omit<Party, 'id'>[];
    }) => {
      const result = await closingDisclosureApi.saveParties(transactionId, parties);
      return result.data;
    },
    onSuccess: (parties, { transactionId }) => {
      // Use getState() to get fresh store reference inside callback
      const store = useClosingDisclosureStore.getState();

      // Update store
      store.updateServerParties(parties);

      // Invalidate parties and list queries
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.parties(transactionId) });
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transactions() });
    },
  });
}

/**
 * Mutation to save line items
 */
export function useSaveLineItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, items }: { transactionId: string; items: LineItem[] }) => {
      const result = await closingDisclosureApi.saveLineItems(transactionId, items);
      return result.data;
    },
    onSuccess: (lineItems, { transactionId }) => {
      // Use getState() to get fresh store reference inside callback
      // This ensures we're always updating the current store state
      const store = useClosingDisclosureStore.getState();

      // Update store with new line items
      store.updateServerLineItems(lineItems);

      // Invalidate line items and list queries
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.lineItems(transactionId) });
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transactions() });
      // Note: Calculations refresh is handled at the page level after all mutations complete
    },
  });
}

/**
 * Mutation to add line items for a specific section
 */
export function useAddLineItemsSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      transactionId,
      section,
      useTemplate = false,
      items,
    }: {
      transactionId: string;
      section: string;
      useTemplate?: boolean;
      items?: Omit<LineItem, 'id' | 'transactionId'>[];
    }) => {
      const result = await closingDisclosureApi.addLineItemsSection(
        transactionId,
        section,
        useTemplate,
        items,
      );
      return result.data;
    },
    onSuccess: (newItems, { transactionId }) => {
      // Use getState() to get fresh store reference inside callback
      const store = useClosingDisclosureStore.getState();

      // Merge with existing line items in store
      const existingItems = store.serverData.lineItems;
      const newSections = new Set(newItems.map((item) => item.section));
      const filteredExisting = existingItems.filter((item) => !newSections.has(item.section));
      store.updateServerLineItems([...filteredExisting, ...newItems]);

      // Invalidate line items and list queries
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.lineItems(transactionId) });
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transactions() });
      // Note: Calculations refresh is handled at the page level after all mutations complete
    },
  });
}

/**
 * Mutation to delete a line item
 */
export function useDeleteLineItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, itemId }: { transactionId: string; itemId: string }) => {
      await closingDisclosureApi.deleteLineItem(transactionId, itemId);
      return { itemId };
    },
    onSuccess: ({ itemId }, { transactionId }) => {
      // Use getState() to get fresh store reference inside callback
      const store = useClosingDisclosureStore.getState();

      // Remove from store
      const existingItems = store.serverData.lineItems;
      store.updateServerLineItems(existingItems.filter((item) => item.id !== itemId));

      // Invalidate line items and list queries
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.lineItems(transactionId) });
      queryClient.invalidateQueries({ queryKey: cdQueryKeys.transactions() });
      // Note: Calculations refresh is handled at the page level after all mutations complete
    },
  });
}

/**
 * Hook to refresh calculations from the server with retry logic
 *
 * Returns a function that can be called after all mutations complete.
 * Includes retry logic to handle eventual consistency from the backend.
 *
 * @example
 * const refreshCalculations = useRefreshCalculations();
 * await refreshCalculations(transactionId); // Uses default retries=2, delay=500ms
 * await refreshCalculations(transactionId, 3, 1000); // Custom retries and delay
 */
export function useRefreshCalculations() {
  const queryClient = useQueryClient();

  return async (transactionId: string, retries = 2, delay = 500): Promise<void> => {
    const store = useClosingDisclosureStore.getState();

    for (let attempt = 0; attempt <= retries; attempt++) {
      if (attempt > 0) {
        // Wait before retry to allow backend to propagate changes
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      try {
        const calcResult = await closingDisclosureApi.getCalculations(transactionId);
        store.updateServerCalculations(calcResult.data);
        queryClient.setQueryData(cdQueryKeys.calculations(transactionId), calcResult.data);
        return; // Success, exit
      } catch (err) {
        console.error('[useRefreshCalculations] Attempt failed:', attempt, err);
        if (attempt === retries) {
          // Final attempt failed, invalidate cache to trigger refetch
          queryClient.invalidateQueries({ queryKey: cdQueryKeys.calculations(transactionId) });
        }
        // Continue to next retry attempt
      }
    }
  };
}

/**
 * Combined mutations object for convenience
 */
export function useCDMutations() {
  return {
    createTransaction: useCreateTransaction(),
    updateTransaction: useUpdateTransaction(),
    saveLoanTerms: useSaveLoanTerms(),
    saveParties: useSaveParties(),
    saveLineItems: useSaveLineItems(),
    addLineItemsSection: useAddLineItemsSection(),
    deleteLineItem: useDeleteLineItem(),
    refreshCalculations: useRefreshCalculations(),
  };
}

export default useCDMutations;
